import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

interface Board {
  target: number; pufDone: number; packedDone: number; awaitingQa: number; overturnPct7d: number | null;
  machinesNotOk: Array<{ code: string; name: string; since: string | null; reason: string | null }>;
  orders: Array<{ id: string; number: string; type: 'so' | 'stock'; productName: string; stage: string }>;
}
const istTime = (iso: string | null) => (iso ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) : '');

function Tile({ label, value, tone }: { label: string; value: string | number; tone?: 'red' }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone === 'red' ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

export function ShiftBoardPanel({ refreshKey = 0 }: { refreshKey?: number }) {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api.get('/manufacturing/shift-board')
      .then((res) => { if (alive) setBoard(res.data.data); })
      .catch((err) => { if (alive) setError(err.response?.data?.error || 'Could not load the live board.'); });
    return () => { alive = false; };
  }, [refreshKey]);

  if (error) return <div className="mb-6 flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"><AlertTriangle className="h-4 w-4" />{error}</div>;
  if (!board) return <div className="mb-6 flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="mb-6">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Live board</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Tile label="Target" value={board.target} />
        <Tile label="PUF done" value={board.pufDone} />
        <Tile label="Awaiting QA" value={board.awaitingQa} />
        <Tile label="Machines not OK" value={board.machinesNotOk.length} tone={board.machinesNotOk.length ? 'red' : undefined} />
        <Tile label="Overturn this week" value={board.overturnPct7d === null ? '—' : `${board.overturnPct7d}%`} />
      </div>
      {board.machinesNotOk.length > 0 && (
        <div role="alert" className="mt-3 space-y-1 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {board.machinesNotOk.map((m) => (
            <p key={m.code} className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {`${m.code} failed its start-of-shift check at ${istTime(m.since)}${m.reason ? ` (${m.reason})` : ''}. Stage moves on it are blocked until a production manager clears it.`}
            </p>
          ))}
        </div>
      )}
      {board.orders.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500">
              <tr><th className="px-3 py-2">Order</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Stage</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {board.orders.map((o) => (
                <tr key={o.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{o.number}</td>
                  <td className="px-3 py-2"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${o.type === 'so' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{o.type === 'so' ? 'SO-driven' : 'Stock plan'}</span></td>
                  <td className="px-3 py-2 text-gray-700">{o.productName}</td>
                  <td className="px-3 py-2 text-gray-700">{o.stage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-xs text-gray-500">Daily production numbers come from production scans; quality from QA scans.</p>
    </div>
  );
}
